import { toast } from '@bozic/ui/toast';
import axios, { type AxiosResponse } from 'axios';
import { isAfter } from 'date-fns';
import { all, call, debounce, put, select, takeLatest } from 'redux-saga/effects';
import { getType, type ActionType } from 'typesafe-actions';

import {
  getDeclaredButterfatTestLimits,
  getDeclaredProteinTestLimits
} from '@/features/drp/drpLimits';

import insuranceAPI from '../../../services/insuranceService';
import mapInsuranceDto from '../../dairyRevenueProtection/mapInsuranceDto';
import mapPracticeDtoToPractice from '../../dairyRevenueProtection/mapPracticeDtoToPractice';
import { loadersActions } from '../../loaders/actions';
import { getOperationName } from '../../operationPage/selectors';
import { scenarioAnalysisActions } from '../actions';
import {
  getScenarioEndorsementCalendarDate,
  getScenarioEndorsementCanSellNote,
  getScenarioEndorsementCoverageLevel,
  getScenarioEndorsementDeclaredButterfatTest,
  getScenarioEndorsementDeclaredClassPriceWeightingFactor,
  getScenarioEndorsementDeclaredComponentPriceWeightingFactor,
  getScenarioEndorsementDeclaredMilkProduction,
  getScenarioEndorsementDeclaredProteinTest,
  getScenarioEndorsementProtectionFactor,
  getScenarioEndorsementQuarter,
  getScenarioEndorsementQuarterList,
  getScenarioEndorsementReinsuranceYear,
  getScenarioEndorsementSalesEffectiveDate,
  getScenarioEndorsementShare,
  getScenarioEndorsementState
} from '../selectors';

import type { InsuranceDto } from '../../../types/dto/InsuranceDto';
import type { PracticeDto } from '../../../types/dto/PracticeDto';
import type { Practice } from '../../../types/Practice';
import type {
  ClassPriceWeightingFactorRestrictedValue,
  ComponentPriceWeightingFactorRestrictedValue
} from '../../dairyRevenueProtection/settings/types';
import type { LegacySagaGenerator } from '../../legacySagaGenerator';

function* initScenarioEndorsementModal(
  action: ActionType<typeof scenarioAnalysisActions.endorsementModal.init>
) {
  try {
    yield put(loadersActions.spinner.activate());
    const { operationName, stateCode } = action.payload;
    const practiceDto: AxiosResponse<PracticeDto> = yield call(
      insuranceAPI.getInsurancePractice,
      operationName,
      stateCode
    );
    const practice: Practice = mapPracticeDtoToPractice(practiceDto);
    yield put(scenarioAnalysisActions.endorsementModal.initModalPracticeData(practice));
    yield call(fetchScenarioEndorsementData);
  } catch (e) {
    console.error(e);
  } finally {
    yield put(loadersActions.spinner.disable());
  }
}

function* fetchScenarioEndorsementData(): LegacySagaGenerator {
  try {
    yield put(loadersActions.spinner.activate());
    const operationName = yield select(getOperationName);

    const stateCode = yield select(getScenarioEndorsementState);
    const quarterName = yield select(getScenarioEndorsementQuarter);
    const share: number = yield select(getScenarioEndorsementShare);

    const quarterList: {
      quarter: string;
      practiceCode: number;
      proteinTest: number;
      butterfatTest: number;
      componentPriceWeightingFactorRestrictedValue: ComponentPriceWeightingFactorRestrictedValue;
      classPriceWeightingFactorRestrictedValue: ClassPriceWeightingFactorRestrictedValue;
    }[] = yield select(getScenarioEndorsementQuarterList);
    let practiceCode: number;
    if (quarterList.findIndex(({ quarter }) => quarter === quarterName) === -1) {
      practiceCode = quarterList[0].practiceCode;
    } else {
      const index = quarterList.findIndex(({ quarter }) => quarter === quarterName);
      practiceCode = quarterList[index].practiceCode;
    }

    const coveredMilkProduction = yield select(getScenarioEndorsementDeclaredMilkProduction);
    const salesEffectiveDate = yield select(getScenarioEndorsementSalesEffectiveDate);
    let coverageLevel = yield select(getScenarioEndorsementCoverageLevel);
    let butterfatTest = yield select(getScenarioEndorsementDeclaredButterfatTest);
    const protectionFactor = yield select(getScenarioEndorsementProtectionFactor);
    let proteinTest = yield select(getScenarioEndorsementDeclaredProteinTest);
    const clsPriceWeightingFactor = yield select(
      getScenarioEndorsementDeclaredClassPriceWeightingFactor
    );
    const cmpPriceWeightingFactor = yield select(
      getScenarioEndorsementDeclaredComponentPriceWeightingFactor
    );
    const reinsuranceYear = yield select(getScenarioEndorsementReinsuranceYear);

    const calendarDate = yield select(getScenarioEndorsementCalendarDate);

    const butterfatTestMin = getDeclaredButterfatTestLimits(calendarDate).min;
    const butterfatTestMax = getDeclaredButterfatTestLimits(calendarDate).max;

    const proteinTestMin = getDeclaredProteinTestLimits(calendarDate).min;
    const proteinTestMax = getDeclaredProteinTestLimits(calendarDate).max;
    if (butterfatTest < butterfatTestMin) {
      yield put(
        scenarioAnalysisActions.endorsementModal.setModalDeclaredButterfatTest(butterfatTestMin)
      );
      butterfatTest = butterfatTestMin;
    }
    if (proteinTest < proteinTestMin) {
      yield put(
        scenarioAnalysisActions.endorsementModal.setModalDeclaredProteinTest(proteinTestMin)
      );
      proteinTest = proteinTestMin;
    }

    if (butterfatTest > butterfatTestMax) {
      yield put(
        scenarioAnalysisActions.endorsementModal.setModalDeclaredButterfatTest(butterfatTestMax)
      );
      butterfatTest = butterfatTestMax;
    }
    if (proteinTest > proteinTestMax) {
      yield put(
        scenarioAnalysisActions.endorsementModal.setModalDeclaredProteinTest(proteinTestMax)
      );
      proteinTest = proteinTestMax;
    }

    if (isAfter(calendarDate, new Date('July 1, 2019'))) {
      if (coverageLevel < 0.8) {
        yield put(scenarioAnalysisActions.endorsementModal.setModalCoverageLevel(0.8));
        coverageLevel = 0.8;
      }
    }

    const practices: {
      practiceCode: number;
      componentPriceWeightingFactorRestrictedValue: ComponentPriceWeightingFactorRestrictedValue;
      classPriceWeightingFactorRestrictedValue: ClassPriceWeightingFactorRestrictedValue;
    }[] = yield select(getScenarioEndorsementQuarterList);
    const practiceIndex = practices.findIndex(practice => practice.practiceCode === practiceCode);
    const componentPriceWeightingFactor =
      practices[practiceIndex]?.componentPriceWeightingFactorRestrictedValue ||
      cmpPriceWeightingFactor;
    const classPriceWeightingFactor =
      practices[practiceIndex]?.classPriceWeightingFactorRestrictedValue || clsPriceWeightingFactor;

    const response: AxiosResponse<InsuranceDto> = yield call(
      insuranceAPI.getInsurance,
      {
        operationName,
        share,
        stateCode,
        practiceCode,
        salesEffectiveDate,
        butterfatTest,
        proteinTest,
        classPriceWeightingFactor,
        coverageLevel,
        coveredMilkProduction,
        protectionFactor,
        componentPriceWeightingFactor
      },
      reinsuranceYear
    );
    const mappedResponse = mapInsuranceDto(response);

    yield put(
      scenarioAnalysisActions.endorsementModal.setModalData({
        expectedRevenue: mappedResponse.priceComponents.expectedRevenue,
        revenueGuarantee: mappedResponse.priceComponents.revenueGuarantee,
        producerPremium: mappedResponse.priceComponents.producerPremiumAmount,
        expectedRevenueCwt: mappedResponse.whatIfDairyComponents.expectedDRPRevenueCwt,
        revenueGuaranteeCwt: mappedResponse.priceComponents.revenueGuaranteeCwt,
        producerPremiumCwt: mappedResponse.priceComponents.producerPremiumAmountCwt
      })
    );
    yield put(scenarioAnalysisActions.endorsementModal.setIsSalesDateAvailable(true));
  } catch (error) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    if (status === 404 || status === 500) {
      const em = yield select(getScenarioEndorsementCanSellNote);
      yield put(scenarioAnalysisActions.endorsementModal.setIsSalesDateAvailable(false));
      toast.error(em ? em : 'Sales date not available', {
        position: 'top-right'
      });
    }
    console.error(error);
  } finally {
    yield put(loadersActions.spinner.disable());
  }
}

function* updatePracticeAndModalData(
  action: ActionType<typeof scenarioAnalysisActions.endorsementModal.setModalCalendarDate>
): LegacySagaGenerator {
  try {
    yield put(loadersActions.spinner.activate());
    const operationName = yield select(getOperationName);
    const stateCode = yield select(getScenarioEndorsementState);
    const salesDate = yield select(getScenarioEndorsementCalendarDate);

    const paddedStateCode = String(stateCode).padStart(2, '0');

    const butterfatTest = yield select(getScenarioEndorsementDeclaredButterfatTest);
    const proteinTest = yield select(getScenarioEndorsementDeclaredProteinTest);
    const coverageLevel = yield select(getScenarioEndorsementCoverageLevel);

    const butterfatTestMin = getDeclaredButterfatTestLimits(action.payload).min;
    const butterfatTestMax = getDeclaredButterfatTestLimits(action.payload).max;

    const proteinTestMin = getDeclaredProteinTestLimits(action.payload).min;
    const proteinTestMax = getDeclaredProteinTestLimits(action.payload).max;

    if (butterfatTest < butterfatTestMin) {
      yield put(
        scenarioAnalysisActions.endorsementModal.setModalDeclaredButterfatTest(butterfatTestMin)
      );
    }
    if (proteinTest < proteinTestMin) {
      yield put(
        scenarioAnalysisActions.endorsementModal.setModalDeclaredProteinTest(proteinTestMin)
      );
    }

    if (butterfatTest > butterfatTestMax) {
      yield put(
        scenarioAnalysisActions.endorsementModal.setModalDeclaredButterfatTest(butterfatTestMax)
      );
    }
    if (proteinTest > proteinTestMax) {
      yield put(
        scenarioAnalysisActions.endorsementModal.setModalDeclaredProteinTest(proteinTestMax)
      );
    }
    if (isAfter(action.payload, new Date('July 1, 2019'))) {
      if (coverageLevel < 0.8) {
        yield put(scenarioAnalysisActions.endorsementModal.setModalCoverageLevel(0.8));
      }
    }

    const practiceDto: AxiosResponse<PracticeDto> = yield call(
      insuranceAPI.getInsurancePracticeSales,
      operationName,
      paddedStateCode,
      salesDate
    );
    const practice: Practice = mapPracticeDtoToPractice(practiceDto);

    yield put(
      scenarioAnalysisActions.endorsementModal.setModalPracticeData({
        salesEffectiveDate: practice.salesEffectiveDate,
        canSell: practice.canSell,
        canSellNote: practice.canSellNote,
        quarterList: practice.practices,
        isSalesDateAvailable: practice.salesEffectiveDate !== null,
        reinsuranceYear: practice.reinsuranceYear
      })
    );

    if (practice.practices.length) {
      const code = yield select(getScenarioEndorsementQuarter);
      if (practice.practices.findIndex(({ quarter }) => quarter === code) === -1) {
        yield put(
          scenarioAnalysisActions.endorsementModal.setModalQuarter(practice.practices[0].quarter)
        );
      } else {
        yield put(scenarioAnalysisActions.endorsementModal.setModalQuarter(code));
      }
    }

    if (!practice.salesEffectiveDate) {
      yield put(scenarioAnalysisActions.endorsementModal.setIsSalesDateAvailable(false));
    }
    yield call(fetchScenarioEndorsementData);
  } catch (e) {
    yield put(scenarioAnalysisActions.endorsementModal.setIsSalesDateAvailable(false));
    console.error(e);
  } finally {
    yield put(loadersActions.spinner.disable());
  }
}

function* scenarioAnalysisEndorsementSettingsSagaWatcher() {
  yield all([
    takeLatest(
      getType(scenarioAnalysisActions.endorsementModal.init),
      initScenarioEndorsementModal
    ),
    debounce(
      800,
      [
        getType(scenarioAnalysisActions.endorsementModal.setModalQuarter),
        getType(scenarioAnalysisActions.endorsementModal.setModalDeclaredMilkProduction),
        getType(scenarioAnalysisActions.endorsementModal.setModalCoverageLevel),
        getType(scenarioAnalysisActions.endorsementModal.setModalProtectionFactor),
        getType(scenarioAnalysisActions.endorsementModal.setModalDeclaredButterfatTest),
        getType(scenarioAnalysisActions.endorsementModal.setModalDeclaredProteinTest),
        getType(scenarioAnalysisActions.endorsementModal.setModalDeclaredClassPriceWeightingFactor),
        getType(
          scenarioAnalysisActions.endorsementModal.setModalDeclaredComponentPriceWeightingFactor
        ),
        getType(scenarioAnalysisActions.endorsementModal.setModalShare)
      ],
      fetchScenarioEndorsementData
    ),
    debounce(
      800,
      [getType(scenarioAnalysisActions.endorsementModal.setModalCalendarDate)],
      updatePracticeAndModalData
    )
  ]);
}

export default scenarioAnalysisEndorsementSettingsSagaWatcher;
